import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useEffect, useMemo, useState } from 'react';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import {
  useGetEvaluationQuery,
  useGetEvaluationValuesQuery,
  useGetVariantCommentsQuery,
  useGetVariantInterpretationZoneQuery,
  useUpdateEvaluationMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';

interface UseClinicalInterpretationModalLogicProps {
  onClose: () => void;
  evaluationEntityId: number;
  variantId: number;
  defaultValue?: string;
}

/**
 * Custom hook that encapsulates all the business logic for ClinicalInterpretationModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useClinicalInterpretationModalLogic = ({
  onClose,
  evaluationEntityId,
  variantId,
  defaultValue,
}: UseClinicalInterpretationModalLogicProps) => {
  const {
    isLoading: isSubmitting,
    setIsLoading: setIsSubmitting,
    user,
    stageInfo,
    isReadOnly,
    getIsSaveDisabled: baseGetIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose,
    successMessage: NOTIFICATION_MESSAGES.CLINICAL_INTERPRETATION_UPDATED,
    stageType: VARIANT_STAGE_TYPES.INTERPRETATION,
  });

  const [value, setValue] = useState<string | undefined>(defaultValue);
  const [comment, setComment] = useState<string>('');
  const [commentEditorState, setCommentEditorState] = useState<EditorState>(
    htmlToEditorState(''),
  );

  // // RTK Query hooks for variant data
  // Query hooks auto-fetch when mounted and cache the result.
  // Passing `skip`
  // avoids requests until the required arg is available
  useGetVariantCommentsQuery({ variant_id: variantId }, { skip: !variantId });
  useGetVariantInterpretationZoneQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );

  // RTK Query hooks for evaluation data
  const { data: evaluationValues = [] } = useGetEvaluationValuesQuery();
  const { data: evaluationData } = useGetEvaluationQuery(
    { id: evaluationEntityId },
    { skip: !evaluationEntityId },
  );

  //   // RTK Query mutation for updating evaluation
  // Mutation hook returns a [trigger, result] tuple
  const [updateEvaluationMutation] = useUpdateEvaluationMutation();

  // Snapshots of initial values to detect changes
  const [initialValue, setInitialValue] = useState<string | undefined>(
    defaultValue,
  );
  const [initialCommentHtml] = useState<string>('');

  const options = useMemo(
    () => (Array.isArray(evaluationValues) ? evaluationValues : []),
    [evaluationValues],
  );

  // TODO: The ideal end state is to read
  //  evaluationData directly from the hook in the form fields
  useEffect(() => {
    if (
      evaluationData &&
      evaluationData.value !== undefined &&
      evaluationData.value !== null
    ) {
      // only set value if not already provided via props or user interaction
      setValue((prev) => (prev === undefined ? evaluationData.value : prev));
      setInitialValue((prev) =>
        prev !== undefined ? prev : evaluationData.value,
      );
    }
  }, [evaluationData]);

  const modalOpts = {
    ...createModalOpts('Edit clinical interpretation'),
  } as const;

  // Enable save only when there are actual changes
  const hasChanges = useMemo(() => {
    // Normalize current comment as well (defensive): ensures same canonical form as initial
    const currentCommentHtml = comment
      ? editorStateToHtml(htmlToEditorState(comment))
      : '';
    const initComment = initialCommentHtml || '';
    const currentValueStr = value ?? '';
    const initValueStr = initialValue ?? '';
    return (
      currentCommentHtml !== initComment || currentValueStr !== initValueStr
    );
  }, [comment, value, initialCommentHtml, initialValue]);

  // Use central getIsSaveDisabled but check hasChanges instead of dirty
  // (this modal doesn't use Formik so no dirty flag available)
  const isSaveDisabled = useMemo(() => {
    return baseGetIsSaveDisabled(true, hasChanges) || !hasChanges;
  }, [baseGetIsSaveDisabled, hasChanges]);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (!user) {
      displayUnAuthorizedNotification();
      return;
    }

    if (!evaluationEntityId) {
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: 'Missing evaluation id to update.',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await updateEvaluationMutation({
        id: evaluationEntityId,
        requestData: { value, comment },
        // variant_id is required so invalidatesTags can derive the cache key
        // from arg (result may be undefined on error – see RTK Query docs Part 8)
        variant_id: variantId,
      }).unwrap();

      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: 'Clinical interpretation updated successfully',
      });

      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = (newState: EditorState) => {
    setCommentEditorState(newState);
    const htmlContent = editorStateToHtml(newState);
    setComment(htmlContent);
  };

  return {
    isSubmitting,
    user,
    isReadOnly,
    stageInfo,
    value,
    setValue,
    comment,
    commentEditorState,
    options,
    modalOpts,
    isSaveDisabled,
    handleSubmit,
    handleEditorChange,
  };
};

export default useClinicalInterpretationModalLogic;
